import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import type {
  ComponentProps,
  FormEventHandler,
} from "react"
import {
  useCallback,
  useState,
} from "react"
import { PasswordForm } from "src/components"
import { useChangePassword } from "src/hooks"
import { UpdatePasswordErrorToast } from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

type Form = ComponentProps<typeof PasswordForm>["form"]

interface Props {
  currentPassword: string;
  onSubmitted: FormEventHandler<HTMLFormElement>;
  children?: ComponentProps<typeof PasswordForm>["children"];
}

export function PasswordPage(props: Props) {
  const {
    onSubmitted,
    currentPassword,
    children,
  } = props

  const changePassword = useChangePassword()
  const { showToast } = useToasterContext()

  const [
    form,
    setForm,
  ] = useState<Form>({
    password: "",
    passwordConfirmation: "",
  })

  const updateForm = useCallback(
    (value: Partial<Form>) => {
      setForm({
        ...form,
        ...value,
      })
    },
    [
      form,
      setForm,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        try {
          await changePassword(
            currentPassword,
            form.password,
          )

          onSubmitted(ev)
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            UpdatePasswordErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            "change-password-error-toast",
            ErrorToast,
          )
        }
      })()
    },
    [
      currentPassword,
      form.password,
      changePassword,
      onSubmitted,
      showToast,
    ],
  )

  return (
    <PasswordForm
      form={form}
      updateForm={updateForm}
      onSubmit={onSubmit}
    >
      {children}
    </PasswordForm>
  )
}
